import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { snagService } from '../../../services/snagService';
import { supabase } from '../../../lib/supabase';
import { siteService } from '../../../services/siteService';
import { useAuth } from '../../../contexts/AuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import MobilePhotoInput from '../../../components/ui/MobilePhotoInput';

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' }
];

const SnagCreateForm = ({ sites, companyId, onSuccess, preselectedSiteId, requirePhotoEvidenceSnag }) => {
  const fileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    location: '',
    priority: 'medium',
    siteId: preselectedSiteId || (sites?.[0]?.id || ''),
    assignedTo: ''
  });
  const { userProfile } = useAuth();
  const { t } = useLanguage();

  const siteOptions = sites?.map(s => ({ value: s?.id, label: s?.site_name })) || [];

  const assigneeOptions = [
    { value: '', label: t('unassigned') },
    ...assignableUsers?.map(u => ({
      value: u?.id,
      label: `${u?.full_name}${u?.role === 'site_manager' ? ` (${t('siteManager')})` : ` (${t('worker')})`}`
    }))
  ];

  useEffect(() => {
    loadAssignableUsers();
  }, []);

  const loadAssignableUsers = async () => {
    try {
      // For site_manager: scope to workers on their assigned sites
      if (userProfile?.role === 'site_manager') {
        const sitesResult = await siteService?.getSitesByUser();
        const siteIds = (sitesResult?.data || [])?.map(s => s?.id);
        if (siteIds?.length > 0) {
          const workersResult = await siteService?.getWorkersBySites(siteIds);
          if (!workersResult?.error && workersResult?.data?.length > 0) {
            setAssignableUsers(workersResult?.data);
            return;
          }
        }
      }

      // All workers and site_managers in the company — including those still
      // pending invite acceptance (is_active=false). Admins need to be able
      // to pre-assign snags before a new hire has accepted their invite. The
      // snags.assigned_to FK still guarantees the reference is valid.
      // jyyb8AfJ — exclude admin-deactivated users so they can't receive new
      // assignments. Existing assignments to a now-deactivated user stay in
      // place per the umbrella's "records retain the user's name" rule.
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, role, email, is_active')
        ?.in('role', ['worker', 'site_manager'])
        ?.is('deactivated_at', null)
        ?.order('full_name');
      if (!error && data) setAssignableUsers(data);
    } catch (err) {
      console.error('Failed to load assignable users:', err);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleFileSelect = (files) => {
    const imageFiles = Array.from(files)?.filter(f => f?.type?.startsWith('image/'));
    setPendingFiles(prev => [...prev, ...imageFiles]);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    handleFileSelect(e?.dataTransfer?.files);
  };

  const removeFile = (index) => {
    setPendingFiles(prev => prev?.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.title?.trim()) { setError(t('titleRequired')); return; }
    if (!form?.description?.trim()) { setError(t('descriptionRequired')); return; }
    if (!form?.siteId) { setError(t('selectSiteRequired')); return; }
    if (requirePhotoEvidenceSnag && pendingFiles?.length === 0) {
      setError('Photo evidence is required — please attach at least one photo before submitting this snag.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Upload photos first
      let photoPaths = [];
      if (pendingFiles?.length > 0) {
        setUploadingPhotos(true);
        for (const file of pendingFiles) {
          const { filePath, error: uploadError } = await snagService?.uploadSnagPhoto(file, companyId, form?.siteId);
          if (!uploadError && filePath) photoPaths?.push(filePath);
        }
        setUploadingPhotos(false);
      }

      const result = await snagService?.createSnag({
        companyId,
        siteId: form?.siteId,
        title: form?.title?.trim(),
        description: form?.description?.trim(),
        location: form?.location?.trim() || null,
        priority: form?.priority,
        assignedTo: form?.assignedTo || null,
        photoPaths
      });

      if (result?.error) {
        setError(result?.error?.message);
      } else {
        setForm({ title: '', description: '', location: '', priority: 'medium', siteId: preselectedSiteId || (sites?.[0]?.id || ''), assignedTo: '' });
        setPendingFiles([]);
        onSuccess?.(result?.data);
      }
    } catch (err) {
      console.error('[SnagCreateForm] create snag failed:', err);
      setError('Failed to create snag. Please try again.');
    } finally {
      setSubmitting(false);
      setUploadingPhotos(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
          <Icon name="AlertCircle" size={16} />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-foreground mb-1.5">{t('title')} <span className="text-destructive">*</span></label>
          <Input
            value={form?.title}
            onChange={e => handleChange('title', e?.target?.value)}
            placeholder={t('snagTitlePlaceholder')}
            required
            className="min-h-[48px] text-base py-3"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">{t('site')} <span className="text-destructive">*</span></label>
          <Select
            options={siteOptions}
            value={form?.siteId}
            onChange={val => handleChange('siteId', val)}
            className="min-h-[48px] text-base"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">{t('priority')}</label>
          <Select
            options={PRIORITY_OPTIONS}
            value={form?.priority}
            onChange={val => handleChange('priority', val)}
            className="min-h-[48px] text-base"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">{t('assignTo')}</label>
          <Select
            options={assigneeOptions}
            value={form?.assignedTo}
            onChange={val => handleChange('assignedTo', val)}
            className="min-h-[48px] text-base"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">{t('locationWithinSite')}</label>
          <Input
            value={form?.location}
            onChange={e => handleChange('location', e?.target?.value)}
            placeholder={t('locationPlaceholder')}
            className="min-h-[48px] text-base py-3"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-foreground mb-1.5">{t('description')} <span className="text-destructive">*</span></label>
          <textarea
            value={form?.description}
            onChange={e => handleChange('description', e?.target?.value)}
            placeholder={t('snagDescriptionPlaceholder')}
            rows={4}
            className="w-full px-3 py-3 text-base bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent resize-none min-h-[100px]"
            required
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-foreground mb-1.5">
            {t('photos')}{requirePhotoEvidenceSnag && <span className="text-destructive ml-1">*</span>}
          </label>
          {requirePhotoEvidenceSnag && (
            <div className="mb-2 flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
              <Icon name="Camera" size={14} className="text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-700 font-medium">Photo evidence required — attach at least one photo.</p>
            </div>
          )}
          <MobilePhotoInput
            label={t('photos')}
            onFilesSelected={(files) => handleFileSelect(files)}
            uploading={uploadingPhotos}
            multiple
            hint="JPEG, PNG, WebP · Max 10MB each"
          />

          {pendingFiles?.length > 0 && (
            <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {pendingFiles?.map((file, idx) => (
                <div key={idx} className="relative group">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`Preview ${idx + 1}`}
                    className="w-full h-20 object-cover rounded-lg border border-border"
                  />
                  <button
                    type="button"
                    onClick={e => { e?.stopPropagation(); removeFile(idx); }}
                    className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center opacity-100 transition-opacity"
                  >
                    <Icon name="X" size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          iconName={submitting ? 'Loader2' : 'Plus'}
          disabled={submitting}
          className="w-full sm:w-auto min-h-[52px] text-base font-semibold"
        >
          {uploadingPhotos ? t('uploadingPhotos') : submitting ? t('creating') : t('createSnag')}
        </Button>
      </div>
    </form>
  );
};

export default SnagCreateForm;
